'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowLeft, Gamepad2, Hand, Navigation, Zap, Volume2, VolumeX,
  Activity, ChevronRight, Map, Cpu, Monitor, Smartphone, Joystick
} from 'lucide-react';
import {
  getSettings, saveSettings,
  type GameSettings, type ControlMode, type PerformanceMode,
  type MinimapDetail, type GlowIntensity
} from '../components/settingsStore';

// ── MOBILE CONTROLS ──
const MOBILE_CONTROLS: {
  id: ControlMode;
  label: string;
  badge: string;
  icon: React.ReactNode;
  description: string;
  details: string[];
  recommended?: string;
}[] = [
  {
    id: 'touch',
    label: 'Full-Screen Touch',
    badge: 'DEFAULT',
    icon: <Hand size={20} className="text-cyan-400" />,
    description: 'Touch anywhere — cell moves toward your finger.',
    details: [
      'Touch anywhere on screen to steer',
      'Hold finger down to keep moving',
      'Drag to change direction smoothly',
      'Best for fast reactive gameplay',
    ],
    recommended: 'Mobile',
  },
  {
    id: 'joystick',
    label: 'Analog Joystick',
    badge: 'CLASSIC',
    icon: <Joystick size={20} className="text-purple-400" />,
    description: 'Floating left-thumb joystick for precise control.',
    details: [
      'Left thumb controls the joystick',
      'Push in any direction to move',
      'Joystick range scales with push distance',
      'Familiar gamepad-style feel',
    ],
    recommended: 'Tablet',
  },
  {
    id: 'tapdrift',
    label: 'Tap-to-Drift',
    badge: 'SMOOTH',
    icon: <Navigation size={20} className="text-green-400" />,
    description: 'Tap a location — cell drifts smoothly toward it.',
    details: [
      'Tap anywhere to set a drift target',
      'Cell glides smoothly toward that point',
      'Tap a new spot to redirect instantly',
      'Relaxed, fluid movement style',
    ],
    recommended: 'Casual',
  },
  {
    id: 'slither',
    label: 'Slither-Style Steer',
    badge: 'DIRECTIONAL',
    icon: <Zap size={20} className="text-yellow-400" />,
    description: 'Classic smooth browser steering — like Slither.io.',
    details: [
      'Touch anywhere to set direction',
      'Cell steers continuously toward touch point',
      'Smooth directional movement',
      'Great for experienced players',
    ],
    recommended: 'Competitive',
  },
  {
    id: 'drag',
    label: 'Drag Steering',
    badge: 'INTUITIVE',
    icon: <Hand size={20} className="text-orange-400" />,
    description: 'Drag anywhere on screen to guide movement.',
    details: [
      'Drag in any direction to move',
      'Drag speed controls movement direction',
      'Continuous guidance while dragging',
      'Natural swipe-based control',
    ],
    recommended: 'Beginners',
  },
  {
    id: 'dualzone',
    label: 'Dual-Zone Control',
    badge: 'ADVANCED',
    icon: <Gamepad2 size={20} className="text-pink-400" />,
    description: 'Left side joystick + right side action zone.',
    details: [
      'Left half of screen = joystick movement',
      'Right half = future boost/action area',
      'Two-thumb control layout',
      'Best for larger phones and tablets',
    ],
    recommended: 'Power Users',
  },
];

// ── DESKTOP CONTROLS ──
const DESKTOP_CONTROLS: {
  id: ControlMode;
  label: string;
  badge: string;
  icon: React.ReactNode;
  description: string;
  details: string[];
}[] = [
  {
    id: 'wasd',
    label: 'WASD Movement',
    badge: 'KEYBOARD',
    icon: <Monitor size={20} className="text-cyan-400" />,
    description: 'Classic WASD keyboard movement.',
    details: ['W = Up', 'A = Left', 'S = Down', 'D = Right'],
  },
  {
    id: 'arrows',
    label: 'Arrow Keys',
    badge: 'KEYBOARD',
    icon: <Monitor size={20} className="text-blue-400" />,
    description: 'Arrow key movement for right-hand players.',
    details: ['↑ = Up', '← = Left', '↓ = Down', '→ = Right'],
  },
  {
    id: 'mouse',
    label: 'Mouse Hold / Steer',
    badge: 'MOUSE',
    icon: <Monitor size={20} className="text-purple-400" />,
    description: 'Hold mouse button and steer toward cursor.',
    details: [
      'Hold left mouse button to move',
      'Move cursor to change direction',
      'Release to stop',
      'Works alongside keyboard controls',
    ],
  },
];

// ── GAMEPAD ──
const GAMEPAD_CONTROL: {
  id: ControlMode;
  label: string;
  badge: string;
  icon: React.ReactNode;
  description: string;
  details: string[];
} = {
  id: 'gamepad',
  label: 'Gamepad / Controller',
  badge: 'ANALOG',
  icon: <Gamepad2 size={20} className="text-green-400" />,
  description: 'Analog stick movement with any connected controller.',
  details: [
    'Left analog stick = movement',
    'Works with Xbox, PlayStation, and generic controllers',
    'Connect controller before starting game',
    'Deadzone auto-calibrated',
  ],
};

const PERFORMANCE_MODES: { id: PerformanceMode; label: string; description: string; color: string }[] = [
  { id: 'low', label: 'Low', description: 'Reduced glow & effects. Best for older devices.', color: 'text-green-400' },
  { id: 'medium', label: 'Medium', description: 'Balanced visuals and performance.', color: 'text-yellow-400' },
  { id: 'high', label: 'High', description: 'Full neon bloom, all effects. Requires a fast device.', color: 'text-red-400' },
];

const MINIMAP_DETAIL: { id: MinimapDetail; label: string; description: string }[] = [
  { id: 'minimal', label: 'Minimal', description: 'Player + mini-bosses only.' },
  { id: 'normal', label: 'Normal', description: 'Player, bosses, large organisms, corruption.' },
  { id: 'full', label: 'Full', description: 'All important entities shown.' },
];

const GLOW_INTENSITY: { id: GlowIntensity; label: string; description: string; color: string }[] = [
  { id: 'off', label: 'Off', description: 'No glow effects. Maximum performance.', color: 'text-gray-400' },
  { id: 'low', label: 'Low', description: 'Subtle glow only.', color: 'text-blue-400' },
  { id: 'medium', label: 'Medium', description: 'Balanced neon glow.', color: 'text-cyan-400' },
  { id: 'high', label: 'High', description: 'Full bloom intensity.', color: 'text-purple-400' },
];

function SectionHeader({ icon, label, color = 'text-cyan-400' }: { icon: React.ReactNode; label: string; color?: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className={color}>{icon}</span>
      <h2 className={`text-sm font-bold font-mono tracking-widest uppercase ${color}`}>{label}</h2>
    </div>
  );
}

function ControlCard({
  mode, isSelected, onClick, showDetails = true
}: {
  mode: typeof MOBILE_CONTROLS[0];
  isSelected: boolean;
  onClick: () => void;
  showDetails?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl transition-all duration-200"
      style={{
        background: isSelected ? 'rgba(0,245,255,0.07)' : 'rgba(255,255,255,0.03)',
        border: isSelected ? '1px solid rgba(0,245,255,0.35)' : '1px solid rgba(255,255,255,0.07)',
        boxShadow: isSelected ? '0 0 20px rgba(0,245,255,0.08)' : 'none',
      }}
    >
      <div className="p-3.5">
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: isSelected ? 'rgba(0,245,255,0.1)' : 'rgba(255,255,255,0.05)' }}
          >
            {mode.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold text-sm font-mono ${isSelected ? 'text-cyan-300' : 'text-gray-200'}`}>
                {mode.label}
              </span>
              <span
                className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded tracking-wider"
                style={{
                  background: isSelected ? 'rgba(0,245,255,0.15)' : 'rgba(255,255,255,0.06)',
                  color: isSelected ? 'rgba(0,245,255,0.9)' : 'rgba(150,150,170,0.7)',
                }}
              >
                {mode.badge}
              </span>
              {'recommended' in mode && mode.recommended && (
                <span className="text-[9px] font-mono text-gray-500">Best for {mode.recommended}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{mode.description}</p>
          </div>
          <div
            className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
            style={{
              borderColor: isSelected ? 'rgba(0,245,255,0.8)' : 'rgba(100,100,120,0.4)',
              background: isSelected ? 'rgba(0,245,255,0.2)' : 'transparent',
            }}
          >
            {isSelected && <div className="w-2 h-2 rounded-full bg-cyan-400" />}
          </div>
        </div>
        {isSelected && showDetails && (
          <div className="mt-3 space-y-1" style={{ paddingLeft: '48px' }}>
            {mode.details.map((d, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <ChevronRight size={10} className="text-cyan-500 mt-0.5 shrink-0" />
                <span className="text-[11px] text-gray-400 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState<GameSettings>({
    controlMode: 'touch',
    performance: 'medium',
    soundEnabled: true,
    showFPS: false,
    minimapDetail: 'normal',
    glowIntensity: 'medium',
    hudCompact: false,
    selectedSkinId: 'luma-prime',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings(getSettings());
  }, []);

  const update = <K extends keyof GameSettings>(key: K, value: GameSettings[K]) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const isMobileControl = ['touch', 'joystick', 'tapdrift', 'slither', 'drag', 'dualzone'].includes(settings.controlMode);
  const isDesktopControl = ['wasd', 'arrows', 'mouse'].includes(settings.controlMode);
  const isGamepadControl = settings.controlMode === 'gamepad';

  return (
    <div className="min-h-screen bg-black text-white" style={{ background: 'linear-gradient(180deg, #050510 0%, #080818 100%)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3"
        style={{
          background: 'rgba(5,5,16,0.92)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,245,255,0.1)',
        }}
      >
        <Link href="/" className="flex items-center gap-1.5 text-gray-400 hover:text-cyan-400 transition-colors text-sm font-mono min-h-[40px] px-2 rounded-lg" style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.1)' }}>
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Home</span>
        </Link>
        <div className="flex-1" />
        <span className="text-xs font-mono text-cyan-500 tracking-widest uppercase">Settings</span>
        {saved && <span className="text-[10px] font-mono text-green-400 animate-pulse">✓ Saved</span>}
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-10">

        {/* ── MOBILE CONTROLS ── */}
        <section>
          <SectionHeader icon={<Smartphone size={16} />} label="Mobile Controls" />
          <div className="space-y-2.5">
            {MOBILE_CONTROLS.map((mode) => (
              <ControlCard
                key={mode.id}
                mode={mode}
                isSelected={settings.controlMode === mode.id}
                onClick={() => update('controlMode', mode.id)}
              />
            ))}
          </div>
        </section>

        {/* ── DESKTOP CONTROLS ── */}
        <section>
          <SectionHeader icon={<Monitor size={16} />} label="Desktop Controls" color="text-blue-400" />
          <div className="space-y-2.5">
            {DESKTOP_CONTROLS.map((mode) => (
              <ControlCard
                key={mode.id}
                mode={mode}
                isSelected={settings.controlMode === mode.id}
                onClick={() => update('controlMode', mode.id)}
              />
            ))}
          </div>
          <div
            className="mt-3 px-4 py-3 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <p className="text-[11px] font-mono text-gray-500 leading-relaxed">
              <span className="text-gray-400 font-bold">Note:</span> WASD and Arrow Keys always work regardless of selected mode. Mouse steer is always active on desktop.
            </p>
          </div>
        </section>

        {/* ── GAMEPAD ── */}
        <section>
          <SectionHeader icon={<Gamepad2 size={16} />} label="Controller / Gamepad" color="text-green-400" />
          <ControlCard
            mode={GAMEPAD_CONTROL}
            isSelected={settings.controlMode === 'gamepad'}
            onClick={() => update('controlMode', 'gamepad')}
          />
          <div
            className="mt-3 px-4 py-3 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <p className="text-[11px] font-mono text-gray-500 leading-relaxed">
              <span className="text-gray-400 font-bold">Browser Support:</span> Connect your controller before starting. Works with most USB/Bluetooth gamepads via the Web Gamepad API.
            </p>
          </div>
        </section>

        {/* ── PERFORMANCE ── */}
        <section>
          <SectionHeader icon={<Activity size={16} />} label="Performance" color="text-purple-400" />
          <div className="grid grid-cols-3 gap-2">
            {PERFORMANCE_MODES.map((p) => {
              const isSelected = settings.performance === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => update('performance', p.id)}
                  className="rounded-xl p-3 text-center transition-all duration-200"
                  style={{
                    background: isSelected ? 'rgba(191,0,255,0.08)' : 'rgba(255,255,255,0.03)',
                    border: isSelected ? '1px solid rgba(191,0,255,0.35)' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className={`text-sm font-bold font-mono mb-1 ${isSelected ? p.color : 'text-gray-400'}`}>{p.label}</div>
                  <div className="text-[10px] text-gray-500 leading-tight">{p.description}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── GLOW INTENSITY ── */}
        <section>
          <SectionHeader icon={<Zap size={16} />} label="Glow Intensity" color="text-yellow-400" />
          <div className="grid grid-cols-4 gap-2">
            {GLOW_INTENSITY.map((g) => {
              const isSelected = settings.glowIntensity === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => update('glowIntensity', g.id)}
                  className="rounded-xl p-3 text-center transition-all duration-200"
                  style={{
                    background: isSelected ? 'rgba(255,215,0,0.06)' : 'rgba(255,255,255,0.03)',
                    border: isSelected ? '1px solid rgba(255,215,0,0.3)' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className={`text-xs font-bold font-mono mb-1 ${isSelected ? g.color : 'text-gray-400'}`}>{g.label}</div>
                  <div className="text-[9px] text-gray-600 leading-tight">{g.description}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── MINIMAP DETAIL ── */}
        <section>
          <SectionHeader icon={<Map size={16} />} label="Minimap Detail" color="text-green-400" />
          <div className="grid grid-cols-3 gap-2">
            {MINIMAP_DETAIL.map((m) => {
              const isSelected = settings.minimapDetail === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => update('minimapDetail', m.id)}
                  className="rounded-xl p-3 text-center transition-all duration-200"
                  style={{
                    background: isSelected ? 'rgba(57,255,20,0.06)' : 'rgba(255,255,255,0.03)',
                    border: isSelected ? '1px solid rgba(57,255,20,0.3)' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div className={`text-xs font-bold font-mono mb-1 ${isSelected ? 'text-green-400' : 'text-gray-400'}`}>{m.label}</div>
                  <div className="text-[9px] text-gray-600 leading-tight">{m.description}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── UI / HUD ── */}
        <section>
          <SectionHeader icon={<Cpu size={16} />} label="UI / HUD" color="text-orange-400" />
          <div className="space-y-2">
            {/* Sound */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex items-center gap-3">
                {settings.soundEnabled ? <Volume2 size={18} className="text-cyan-400" /> : <VolumeX size={18} className="text-gray-500" />}
                <div>
                  <div className="text-sm font-mono font-bold text-gray-200">Sound</div>
                  <div className="text-[10px] text-gray-500">Game audio effects</div>
                </div>
              </div>
              <button
                onClick={() => update('soundEnabled', !settings.soundEnabled)}
                className="relative w-11 h-6 rounded-full transition-all duration-200"
                style={{
                  background: settings.soundEnabled ? 'rgba(0,245,255,0.3)' : 'rgba(255,255,255,0.1)',
                  border: settings.soundEnabled ? '1px solid rgba(0,245,255,0.5)' : '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <div
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-all duration-200"
                  style={{
                    left: settings.soundEnabled ? '22px' : '2px',
                    background: settings.soundEnabled ? '#00f5ff' : 'rgba(150,150,170,0.5)',
                  }}
                />
              </button>
            </div>

            {/* Show FPS */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex items-center gap-3">
                <Activity size={18} className={settings.showFPS ? 'text-green-400' : 'text-gray-500'} />
                <div>
                  <div className="text-sm font-mono font-bold text-gray-200">Show FPS</div>
                  <div className="text-[10px] text-gray-500">Display frame rate in HUD</div>
                </div>
              </div>
              <button
                onClick={() => update('showFPS', !settings.showFPS)}
                className="relative w-11 h-6 rounded-full transition-all duration-200"
                style={{
                  background: settings.showFPS ? 'rgba(57,255,20,0.3)' : 'rgba(255,255,255,0.1)',
                  border: settings.showFPS ? '1px solid rgba(57,255,20,0.5)' : '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <div
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-all duration-200"
                  style={{
                    left: settings.showFPS ? '22px' : '2px',
                    background: settings.showFPS ? '#39ff14' : 'rgba(150,150,170,0.5)',
                  }}
                />
              </button>
            </div>

            {/* HUD Compact */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex items-center gap-3">
                <Monitor size={18} className={settings.hudCompact ? 'text-orange-400' : 'text-gray-500'} />
                <div>
                  <div className="text-sm font-mono font-bold text-gray-200">HUD Compact Mode</div>
                  <div className="text-[10px] text-gray-500">Reduce HUD element sizes</div>
                </div>
              </div>
              <button
                onClick={() => update('hudCompact', !settings.hudCompact)}
                className="relative w-11 h-6 rounded-full transition-all duration-200"
                style={{
                  background: settings.hudCompact ? 'rgba(255,102,0,0.3)' : 'rgba(255,255,255,0.1)',
                  border: settings.hudCompact ? '1px solid rgba(255,102,0,0.5)' : '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <div
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-all duration-200"
                  style={{
                    left: settings.hudCompact ? '22px' : '2px',
                    background: settings.hudCompact ? '#ff6600' : 'rgba(150,150,170,0.5)',
                  }}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Back to game */}
        <div className="pb-8">
          <Link
            href="/"
            className="block w-full text-center py-3 rounded-xl font-mono font-bold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'rgba(0,245,255,0.08)',
              border: '1px solid rgba(0,245,255,0.3)',
              color: 'rgba(0,245,255,0.9)',
              boxShadow: '0 0 20px rgba(0,245,255,0.08)',
            }}
          >
            ▶ Enter Arena
          </Link>
        </div>
      </div>
    </div>
  );
}
